import { useEffect, useRef, useState } from "react";

/**
 * Names a new golden dataset. Replaces window.prompt so the flow keeps the
 * dashboard's styling, explains what seeding does, and can show a busy state
 * while the CSV import runs.
 */
export default function DatasetDialog({
  open,
  shouldSeedFromCorpus,
  busy,
  onCancel,
  onConfirm,
}) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(shouldSeedFromCorpus ? "Golden corpus (imported)" : "");
    inputRef.current?.focus();
  }, [open, shouldSeedFromCorpus]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel, open]);

  if (!open) {
    return null;
  }

  const submit = (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <div className="mse-modal" role="presentation" onMouseDown={onCancel}>
      <form
        className="mse-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Create golden dataset"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={submit}
      >
        <h3 className="mse-modal__title">
          {shouldSeedFromCorpus
            ? "Import the golden CSV"
            : "New golden dataset"}
        </h3>
        <p className="mse-modal__body">
          {shouldSeedFromCorpus
            ? "Creates a dataset pre-filled with every loan in the checked-in golden-loans CSV, including the human-reviewed income used to score accuracy."
            : "Creates an empty dataset. Add loans to it from the One loan tab, or when you clone a loan from production."}
        </p>
        <label className="mse-field">
          <span className="mse-field__label">Dataset name</span>
          <input
            ref={inputRef}
            className="mse-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Self-employed regression set"
          />
        </label>
        <div className="mse-modal__actions">
          <button
            type="button"
            className="mse-btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="mse-btn mse-btn--primary"
            disabled={busy || !name.trim()}
          >
            {busy
              ? "Working…"
              : shouldSeedFromCorpus
                ? "Import dataset"
                : "Create dataset"}
          </button>
        </div>
      </form>
    </div>
  );
}
